import { type CardProviderOption, type FuelCardMatching, FuelCardRequester, type FuelTransaction, GraphQLFuelCardRequester, NewFuelCardRequester } from '@cobliteam/api-lib';
import type { ImportFuelTransactionsBatchInput, ValidateFuelTransactionsImportInput, ValidateFuelTransactionsImportResponse } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { UpdateTransactionRequest } from '@cobliteam/api-lib/src/dashboard/fuelCard/Definitions/APITypes';
import type {
  FuelTransactionBodyContent,
  PagedTransactionStatsByVehicle,
  PagedTransactions,
  TransactionStatsByVehicleSummary,
  TransactionSummaryBodyContent,
  TransactionSummaryBodyContentByPeriodType,
  TransactionsAvgFuelConsumptionAndCostPerKm,
  TransactionsHistoryBodyContent,
  TransactionsSummary,
  TransactionsSumTotalCostByFuelType,
  UUID,
} from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getFuelCardRequester = () => new FuelCardRequester({ host: env.herbieApiHost });
const getNewFuelCardRequester = () => new NewFuelCardRequester({ host: env.apiHost });
const getGraphQLFuelCardRequester = () => new GraphQLFuelCardRequester();

const FUEL_TRANSACTIONS_QUERY_KEY = 'fuelTransactions';
const FUELCARD_ACTIVE_QUERY_KEY = 'fuelCardActive';
const FUELCARD_INACTIVE_QUERY_KEY = 'fuelCardInactive';

const useFuelCard = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('fuel-card');

  const getActiveFuelCardMatchings = () => {
    return useQuery<FuelCardMatching[]>({
      queryKey: [`${FUELCARD_ACTIVE_QUERY_KEY}`],
      queryFn: () => getFuelCardRequester().loadFuelCardMatchings('active'),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const getInactiveFuelCardMatchings = () => {
    return useQuery<FuelCardMatching[]>({
      queryKey: [`${FUELCARD_INACTIVE_QUERY_KEY}`],
      queryFn: () => getFuelCardRequester().loadFuelCardMatchings('inactive'),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const getFuelTransaction = (transactionId: string) => {
    return useQuery<FuelTransaction>({
      queryKey: ['fuelTransaction', transactionId],
      queryFn: () => getNewFuelCardRequester().getFuelTransaction(transactionId),
    });
  };

  const getTransactionsHistory = (input: TransactionsHistoryBodyContent) => {
    return useQuery<PagedTransactions>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, input],
      queryFn: () => getNewFuelCardRequester().getTransactionsHistory(input),
    });
  };

  const getTransactionsSummary = (input: TransactionSummaryBodyContent) => {
    return useQuery<TransactionsSummary>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'summary', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsSummary(input),
    });
  };

  const getTransactionStatsByVehicle = (input: TransactionsHistoryBodyContent) => {
    return useQuery<PagedTransactionStatsByVehicle>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicle(input),
    });
  };

  const getTransactionStatsByVehicleSummary = (input: TransactionSummaryBodyContent) => {
    return useQuery<TransactionStatsByVehicleSummary>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle-summary', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicleSummary(input),
    });
  };

  const getTransactionsAverageFuelComsumptionAndCostPerKm = (input: TransactionSummaryBodyContentByPeriodType) => {
    return useQuery<TransactionsAvgFuelConsumptionAndCostPerKm[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'avg-fuel-consumption-and-cost-per-km', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsAvgFuelConsumptionAndCostPerKm(input),
    });
  };

  const getTransactionsSumTotalCostByFuelType = (input: TransactionSummaryBodyContentByPeriodType) => {
    return useQuery<TransactionsSumTotalCostByFuelType[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'sum-total-cost-by-fuel-type', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsSumTotalCostByFuelType(input),
    });
  };

  const deleteFuelCardMatchings = useMutation<void, unknown, { ids: UUID[] }>({
    mutationFn: (fuelCardDeleteContent) => getFuelCardRequester().deactivateFuelCards(fuelCardDeleteContent.ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY] });
    },
  });

  const recoverFuelCardMatchings = useMutation<void, unknown, { ids: UUID[] }>({
    mutationFn: (fuelCardRecoverContent) => getFuelCardRequester().activateFuelCards(fuelCardRecoverContent.ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY] });
    },
  });

  const importTransactions = () =>
    useMutation<void, unknown, FuelTransactionBodyContent>({
      mutationFn: (data) => getNewFuelCardRequester().createFuelTransaction(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [FUEL_TRANSACTIONS_QUERY_KEY] });
      },
    });

  const deleteTransactions = useMutation<void, unknown, { transactionIds: string[] }>({
    mutationFn: (data) => getFuelCardRequester().deleteTransactions(data.transactionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUEL_TRANSACTIONS_QUERY_KEY] });
    },
  });

  const requestIntegration = useMutation<void, unknown, { provider: CardProviderOption; cnpj?: string; email?: string }>({
    mutationFn: (data) => getFuelCardRequester().requestIntegration(data.provider, data.cnpj, data.email),
  });

  const updateFuelTransaction = useMutation<void, unknown, { transactionId: string; transactionData: UpdateTransactionRequest }>({
    mutationFn: (data) => getNewFuelCardRequester().updateFuelTransaction(data.transactionId, data.transactionData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUEL_TRANSACTIONS_QUERY_KEY] });
    },
  });

  const saveFuelCardMatching = useMutation<void, unknown, { id: UUID; licensePlate?: string }>({
    mutationFn: (data) => getFuelCardRequester().saveFuelCardMatching(data.id, data.licensePlate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY] });
    },
  });

  const useValidateFuelTransactionsImport = (input: ValidateFuelTransactionsImportInput | null) => {
    return useQuery<ValidateFuelTransactionsImportResponse | undefined>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'validate-import', input],
      queryFn: () => {
        if (!input) return undefined;
        return getGraphQLFuelCardRequester().validateFuelTransactionsImport(input);
      },
      enabled: !!input,
    });
  };

  const useBatchImportFuelTransactions = () => {
    return useMutation({
      mutationFn: (input: ImportFuelTransactionsBatchInput[]) => getGraphQLFuelCardRequester().importFuelTransactions(input),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [FUEL_TRANSACTIONS_QUERY_KEY],
        });
      },
      onError: () => Alert.error(t('sheet_import.import_error')),
    });
  };

  return {
    getFuelTransaction,
    getTransactionsHistory,
    getTransactionsSummary,
    getTransactionStatsByVehicle,
    getTransactionStatsByVehicleSummary,
    getTransactionsAverageFuelComsumptionAndCostPerKm,
    getTransactionsSumTotalCostByFuelType,
    getActiveFuelCardMatchings,
    getInactiveFuelCardMatchings,
    importTransactions,
    deleteFuelCardMatchings,
    recoverFuelCardMatchings,
    deleteTransactions,
    requestIntegration,
    updateFuelTransaction,
    saveFuelCardMatching,
    useValidateFuelTransactionsImport,
    useBatchImportFuelTransactions,
  };
};

export default useFuelCard;
